import { Injectable, computed, effect, inject } from '@angular/core';
import { signal } from '@angular/core';
import { InvoiceStatus } from '@lb/api/mylb/enums/invoice-status.enum';
import { IInvoiceListItem, IInvoiceFilterOptions } from '@lb/api/mylb/interfaces';
import { InvoiceService } from '@lb/api/mylb/services';
import { UserStateService } from '@lb/state/user-state.service';
import { InvoiceStateService } from '@lb/state/invoice-state.service';
import { take } from 'rxjs/internal/operators/take';

@Injectable({
	providedIn: 'root'
})
export class InvoiceGridService {
	private _invoiceService = inject(InvoiceService);
	private _userSummaryState = inject(UserStateService);
	private _invoiceStateService = inject(InvoiceStateService);

	/**
	 * Signal to track the list of invoices
	 */
	private _invoices = signal<IInvoiceListItem[]>([]);

	/**
	 * Signal to track the total count of invoices (for pagination)
	 */
	private _invoiceCount = signal<number>(0);

	/**
	 * Signal to track whether to show the instructor column
	 */
	private _showInstructor = signal<boolean>(false);

	/**
	 * Signal to track the current filter options
	 */
	private _filters = signal<IInvoiceFilterOptions>({
		page: 1,
		pageSize: 25
	});

	/**
	 * Signal to track loading state
	 */
	loading = signal<boolean>(false);

	/**
	 * Signal to track error state
	 */
	error = signal<string | null>(null);

	/**
	 * Signal to track if this is the first effect run
	 */
	private _isInitialized = signal<boolean>(false);

	public invoices = this._invoices.asReadonly();
	public filters = this._filters.asReadonly();
	public invoiceCount = this._invoiceCount.asReadonly();
	public invoiceStatuses = this._invoiceStateService.invoiceStatuses;
	public showInstructor = this._showInstructor.asReadonly();
	public currentPage = computed(() => this._filters().page);
	public itemsPerPage = computed(() => this._filters().pageSize);
	public isInitialized = this._isInitialized.asReadonly();

	constructor() {
		// Effect to fetch invoices when filters change
		effect(() => {
			// Skip the until initialized check on the first run to avoid unnecessary fetch
			if (!this._isInitialized()) {
				return;
			}

			const filters = this._filters();
			this._fetchInvoices(filters);
		});
	}

	/**
	 * Initialize the service with optional initial filter overrides
	 * Must be called once to trigger the first data fetch
	 * @param initialFilters Optional filter overrides to merge with defaults
	 */
	initialize(initialFilters?: Partial<IInvoiceFilterOptions>) {
		// Set initial filters if provided
		if (initialFilters) {
			this._filters.set({
				page: 1,
				pageSize: 25,
				...initialFilters
			});
		}

		// Mark as initialized - this will trigger the effect to fetch
		this._isInitialized.set(true);
	}

	/**
	 * Fetch invoices from the API based on current filters
	 */
	private _fetchInvoices(filters: IInvoiceFilterOptions) {
		this.loading.set(true);
		this.error.set(null);

		this._invoiceService
			.getInvoices(filters)
			.pipe(take(1))
			.subscribe({
				next: (response) => {
					this._invoices.set(response.items);
					this._invoiceCount.set(response.totalCount);

					if (this.showInstructor() && response.totalCount > 0) {
						this.fetchUserSummaries();
					} else {
						this.loading.set(false);
					}
				},
				error: (err) => {
					this.error.set(err?.message || 'Failed to load invoices');
					this.loading.set(false);
					console.error('Error fetching invoices:', err);
				}
			});
	}

	/**
	 * Fetch user summaries for displayed instructors
	 * Call this conditionally based on your requirements
	 */
	fetchUserSummaries() {
		const currentInvoices = this._invoices();

		if (!currentInvoices || currentInvoices.length === 0) {
			return;
		}

		// Extract unique instructor user IDs
		const instructorIds = [...new Set(currentInvoices.map((inv) => inv.instructorUserID))];

		if (instructorIds.length === 0) {
			return;
		}

		// Load summaries (cached automatically)
		this._userSummaryState.loadUserSummaries(instructorIds);
		this.loading.set(false);
	}

	/**
	 * Set whether to show the instructor column and load user summaries if showing
	 */
	setShowInstructor(show: boolean) {
		this._showInstructor.set(show);
	}

	/**
	 * Get the current invoices signal
	 */
	getInvoices() {
		return this.invoices;
	}

	/**
	 * Get the current filters signal
	 */
	getFilters() {
		return this.filters;
	}

	/**
	 * Clear all invoices
	 */
	clearInvoices() {
		this._invoices.set([]);
	}

	/**
	 * Update filter options (triggers API call automatically).
	 * Keys set to `undefined` in `filterUpdates` are removed from the active filters,
	 * allowing individual filters to be cleared without affecting others.
	 */
	updateFilters(filterUpdates: Partial<IInvoiceFilterOptions>) {
		this._filters.update((current) => {
			const merged = {
				...current,
				...filterUpdates,
				page: filterUpdates.page ?? 1
			};
			return Object.fromEntries(Object.entries(merged).filter(([, v]) => v !== undefined)) as unknown as IInvoiceFilterOptions;
		});
	}

	/**
	 * Reload invoices using current filters
	 */
	reloadInvoices() {
		if (!this._isInitialized()) {
			return;
		}

		this._fetchInvoices(this._filters());
	}

	/**
	 * Reset filters to default state and trigger API call, "In Progress" statuses by default
	 */
	resetFilters() {
		this._filters.set({
			invoiceStatusIDs: [InvoiceStatus.Draft, InvoiceStatus.Ready, InvoiceStatus.Submitted],
			page: 1,
			pageSize: 25
		});
	}

	/**
	 * Clear all state
	 */
	clearState() {
		this._invoices.set([]);
		this._invoiceCount.set(0);
		this._filters.set({
			page: 1,
			pageSize: 20
		});
		this.loading.set(false);
		this.error.set(null);
	}
}
